import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  UserPlus,
  Download,
  MapPin,
  MessageSquare,
  Package,
  Bell,
  History,
  Camera,
  Smartphone,
  Landmark,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  Lock,
  DollarSign,
} from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Footer } from '@/sections/Footer';
import { Phone } from '@/components/system';
import { SEO } from '@/components/SEO';

gsap.registerPlugin(ScrollTrigger);

const steps = [
  {
    number: '01',
    icon: Download,
    title: 'Descarga Mi Envío Cuba',
    description:
      'La app Mi Envío Cuba llega muy pronto a App Store y Google Play — gratis y en español. Tu agencia te avisará en cuanto esté disponible.',
    details: [
      'Gratis y en español',
      'Muy pronto en App Store y Google Play',
      'Instalación en minutos',
    ],
  },
  {
    number: '02',
    icon: UserPlus,
    title: 'Crea tu cuenta y elige tu agencia',
    description:
      'Regístrate en tres pasos con verificación por correo — o deja que tu agencia cree tu cuenta por ti. Luego elige tu agencia principal y verás sus tarifas, sucursales y servicios.',
    details: [
      'Registro en 3 pasos con verificación por correo',
      'Número de cliente permanente',
      'Tu agencia principal, siempre a mano',
    ],
  },
  {
    number: '03',
    icon: MapPin,
    title: 'Haz seguimiento en tiempo real',
    description:
      'Sigue el estado de tus envíos a medida que avanzan, artículo por artículo. Recibe notificaciones automáticas con cada actualización.',
    details: [
      'Estado del envío actualizado al instante',
      'Notificaciones push automáticas',
      'Prueba de entrega: foto, firma y GPS',
    ],
  },
  {
    number: '04',
    icon: MessageSquare,
    title: 'Comunícate directo',
    description:
      'Escríbele a tu agencia desde la app cuando quieras. Sin llamadas interminables, sin buscar el número de WhatsApp — todo en un solo lugar.',
    details: [
      'Chat directo con tu agencia',
      'Sin necesidad de llamar',
      'Historial de conversaciones',
    ],
  },
];

const clientFeatures = [
  {
    icon: Package,
    title: 'Rastreo en tiempo real',
    description: 'Ve el estado de cada envío, artículo por artículo, desde que sale hasta que llega.',
  },
  {
    icon: Bell,
    title: 'Notificaciones automáticas',
    description: 'Recibe alertas en tu teléfono con cada actualización sin tener que preguntar.',
  },
  {
    icon: MessageSquare,
    title: 'Chat con tu agencia',
    description: 'Comunícate directamente con tu agencia desde la app. Sin WhatsApp, sin llamadas.',
  },
  {
    icon: History,
    title: 'Historial de envíos',
    description: 'Consulta el registro completo de tus envíos anteriores, con su prueba de entrega.',
  },
  {
    icon: Camera,
    title: 'Prueba de entrega verificada',
    description: 'Foto, firma del receptor y GPS en cada entrega, visibles en tu teléfono.',
  },
  {
    icon: Landmark,
    title: 'Cargos aduanales',
    description: 'Consulta los Cargos aduanales oficiales de cualquier artículo antes de enviarlo.',
  },
];

const faqs = [
  {
    question: '¿Tengo que pagar para usar la app?',
    answer:
      'No. La app es completamente gratuita para los clientes. Es tu agencia quien contrata la plataforma MEC — tú solo la usas.',
  },
  {
    question: '¿Cómo descargo la app?',
    answer:
      'La app se llama Mi Envío Cuba y llegará muy pronto a App Store y Google Play. En cuanto esté disponible, descárgala gratis, crea tu cuenta y elige tu agencia dentro de la app.',
  },
  {
    question: '¿Mis datos están seguros?',
    answer:
      'Sí. Tu información personal se guarda cifrada y se usa únicamente para gestionar tus envíos y servicios con tu agencia.',
  },
  {
    question: '¿Qué más puedo hacer desde la app?',
    answer:
      'Además de rastrear tus envíos, puedes programar una recogida a domicilio, pre-alertar cajas que enviaste por tu cuenta con Casillero Envíos, consultar los Cargos aduanales oficiales de cualquier artículo y escribirle a tu agencia por chat.',
  },
];

function FAQItem({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white rounded-[20px] shadow-card overflow-hidden">
      <button
        className="w-full flex items-center justify-between px-6 py-5 text-left gap-4"
        onClick={() => setOpen(!open)}
      >
        <span className="font-heading text-base font-semibold text-text-primary">
          {question}
        </span>
        {open ? (
          <ChevronUp className="w-5 h-5 text-brand flex-shrink-0" />
        ) : (
          <ChevronDown className="w-5 h-5 text-text-muted flex-shrink-0" />
        )}
      </button>
      {open && (
        <div className="px-6 pb-5">
          <p className="text-text-secondary text-sm leading-relaxed">{answer}</p>
        </div>
      )}
    </div>
  );
}

export function Clientes() {
  const headerRef = useRef<HTMLDivElement>(null);
  const stepsRef = useRef<HTMLDivElement>(null);
  const featuresRef = useRef<HTMLDivElement>(null);
  const faqRef = useRef<HTMLDivElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        headerRef.current,
        { y: 30, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.6,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: headerRef.current,
            start: 'top 80%',
          },
        }
      );

      const stepCards = stepsRef.current?.querySelectorAll('.step-card');
      if (stepCards) {
        gsap.fromTo(
          stepCards,
          { y: 50, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            stagger: 0.15,
            duration: 0.6,
            ease: 'power2.out',
            scrollTrigger: {
              trigger: stepsRef.current,
              start: 'top 75%',
            },
          }
        );
      }

      const featureCards = featuresRef.current?.querySelectorAll('.feature-card');
      if (featureCards) {
        gsap.fromTo(
          featureCards,
          { y: 30, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            stagger: 0.08,
            duration: 0.5,
            ease: 'power2.out',
            scrollTrigger: {
              trigger: featuresRef.current,
              start: 'top 80%',
            },
          }
        );
      }

      gsap.fromTo(
        faqRef.current,
        { y: 30, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.6,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: faqRef.current,
            start: 'top 80%',
          },
        }
      );

      gsap.fromTo(
        ctaRef.current,
        { y: 30, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.6,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: ctaRef.current,
            start: 'top 85%',
          },
        }
      );
    });

    return () => ctx.revert();
  }, []);

  return (
    <main className="bg-bg-light">
      <SEO
        title="Clientes | Mi Envío Cuba — La app para tus envíos"
        description="Con la app Mi Envío Cuba, conectada a tu agencia, rastreas tus paquetes artículo por artículo, con prueba de entrega verificada y chat directo. Gratis para clientes."
        path="/clientes"
        keywords="rastrear paquetes Cuba, app seguimiento envíos Cuba, app agencia envíos, seguimiento paquetes tiempo real Cuba, app cliente courier Cuba, app Mi Envío Cuba"
      />

      {/* Hero */}
      <div ref={headerRef} className="pt-36 sm:pt-44 pb-16 px-4 sm:px-6 lg:px-8 xl:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-semibold tracking-[-0.03em] text-text-primary mb-6">
            Tu agencia, ahora en tu bolsillo.
          </h1>
          <p className="text-xl sm:text-2xl text-text-secondary font-medium leading-snug mb-10 max-w-2xl mx-auto">
            Con la app Mi Envío Cuba, conectada a tu agencia, rastreas tus envíos en
            tiempo real, solicitas servicios y te comunicas con ellos directamente desde
            tu teléfono. Sin llamadas, sin WhatsApp, sin incertidumbre.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              className="rounded-full bg-brand text-white px-7 h-12 text-[17px] hover:bg-brand-deep transition-colors group"
              onClick={() => document.getElementById('como-funciona-para-ti')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
            >
              Ver cómo funciona
              <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button
              asChild
              variant="outline"
              className="rounded-full px-7 h-12 text-[17px] border-text-primary/20 text-text-primary hover:bg-text-primary/5 transition-colors"
            >
              <Link to="/plataforma">
                ¿Eres dueño de una agencia?
              </Link>
            </Button>
          </div>

          {/* Real app screens */}
          <div className="relative mt-14 flex items-start justify-center gap-5 sm:gap-8">
            <div className="hidden sm:block translate-y-12">
              <Phone src="/screens/mec-user-paquete.png" alt="Mi Envío Cuba — detalle de un paquete con su recorrido etapa por etapa" width={230} />
            </div>
            <Phone src="/screens/mec-user-home.png" alt="Mi Envío Cuba — inicio con el estado de tus envíos activos" width={260} priority />
            <div className="hidden sm:block translate-y-12">
              <Phone src="/screens/mec-user-servicios.png" alt="Mi Envío Cuba — servicios de tu agencia en la app" width={230} />
            </div>
          </div>
        </div>
      </div>

      {/* How it works for clients */}
      <div id="como-funciona-para-ti" ref={stepsRef} className="pb-16 px-4 sm:px-6 lg:px-8 xl:px-12 scroll-mt-24">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-[-0.03em] text-text-primary mb-4">
              Cómo funciona para ti
            </h2>
            <p className="text-text-secondary max-w-xl mx-auto">
              Cuatro pasos simples para empezar a gestionar tus envíos desde donde estés.
            </p>
          </div>
          <div className="space-y-8">
            {steps.map((step, index) => (
              <div
                key={index}
                className="step-card bg-white rounded-[28px] shadow-card p-8 lg:p-10"
              >
                <div className="flex flex-col lg:flex-row gap-6 lg:gap-10">
                  {/* Icon & Number */}
                  <div className="flex items-start gap-4">
                    <div className="w-16 h-16 rounded-2xl bg-brand flex items-center justify-center flex-shrink-0">
                      <step.icon className="w-8 h-8 text-white" />
                    </div>
                    <span className="font-heading text-5xl font-bold text-brand/20 lg:hidden">
                      {step.number}
                    </span>
                  </div>

                  {/* Content */}
                  <div className="flex-1">
                    <div className="flex items-center gap-4 mb-4">
                      <h3 className="font-heading text-2xl font-semibold text-text-primary">
                        {step.title}
                      </h3>
                      <span className="hidden lg:block font-heading text-4xl font-bold text-brand/20">
                        {step.number}
                      </span>
                    </div>
                    <p className="text-text-secondary leading-relaxed mb-6">
                      {step.description}
                    </p>
                    <ul className="space-y-2">
                      {step.details.map((detail, i) => (
                        <li
                          key={i}
                          className="flex items-center gap-2 text-sm text-text-secondary"
                        >
                          <div className="w-1.5 h-1.5 rounded-full bg-brand" />
                          {detail}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Client Features */}
      <div ref={featuresRef} className="pb-16 px-4 sm:px-6 lg:px-8 xl:px-12">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-[-0.03em] text-text-primary mb-4">
              Lo que tienes en tu app
            </h2>
            <p className="text-text-secondary max-w-xl mx-auto">
              Todo lo que necesitas para gestionar tus envíos y comunicarte con tu agencia,
              desde la palma de tu mano.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {clientFeatures.map((feature, index) => (
              <div
                key={index}
                className="feature-card bg-white rounded-[24px] shadow-card p-6"
              >
                <div className="w-12 h-12 rounded-xl bg-brand/10 flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-brand" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-text-primary mb-2">
                  {feature.title}
                </h3>
                <p className="text-text-secondary text-sm">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Trust strip */}
      <div className="pb-16 px-4 sm:px-6 lg:px-8 xl:px-12">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-[28px] shadow-card p-8 lg:p-10">
            <div className="grid sm:grid-cols-3 gap-8 text-center">
              <div className="flex flex-col items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-brand/10 flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-brand" />
                </div>
                <h3 className="font-heading text-base font-semibold text-text-primary">Gratis para clientes</h3>
                <p className="text-text-secondary text-sm">
                  La app no tiene costo para ti. Tu agencia es quien la contrata.
                </p>
              </div>
              <div className="flex flex-col items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-brand/10 flex items-center justify-center">
                  <Lock className="w-6 h-6 text-brand" />
                </div>
                <h3 className="font-heading text-base font-semibold text-text-primary">Datos cifrados</h3>
                <p className="text-text-secondary text-sm">
                  Tu información viaja y se guarda cifrada.
                </p>
              </div>
              <div className="flex flex-col items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-brand/10 flex items-center justify-center">
                  <Smartphone className="w-6 h-6 text-brand" />
                </div>
                <h3 className="font-heading text-base font-semibold text-text-primary">Muy pronto en App Store y Google Play</h3>
                <p className="text-text-secondary text-sm">
                  Para iPhone y Android, sin importar tu teléfono.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ */}
      <div ref={faqRef} className="pb-16 px-4 sm:px-6 lg:px-8 xl:px-12">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-[-0.03em] text-text-primary text-center mb-10">
            Preguntas frecuentes
          </h2>
          <div className="space-y-3">
            {faqs.map((faq, index) => (
              <FAQItem key={index} question={faq.question} answer={faq.answer} />
            ))}
          </div>
        </div>
      </div>

      {/* Closing CTA — for agency owners who land here */}
      <div ref={ctaRef} className="pb-24 px-4 sm:px-6 lg:px-8 xl:px-12">
        <div className="max-w-3xl mx-auto bg-white rounded-[28px] shadow-card p-8 lg:p-10 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-[-0.03em] text-text-primary mb-4">
            ¿Tu agencia todavía no usa MEC?
          </h2>
          <p className="text-text-secondary mb-8 max-w-xl mx-auto">
            Si tu agencia aún gestiona todo por WhatsApp o llamadas, cuéntales sobre MEC.
            Pueden tener su propio panel de gestión, presencia en la app Mi Envío Cuba y mucho más.
          </p>
          <Button
            asChild
            className="rounded-full bg-brand text-white px-7 h-12 text-[17px] hover:bg-brand-deep transition-colors group"
          >
            <Link to="/plataforma">
              Ver información para agencias
              <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </Link>
          </Button>
        </div>
      </div>

      <Footer />
    </main>
  );
}
